import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TradierService } from '../../services/TradierService';
import { useToast } from '../../hooks/useToast';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { StockQuote } from '../../types/market';

interface MarketIndex { symbol: string; name: string; quote?: StockQuote; }

const MARKET_INDICES: MarketIndex[] = [
  { symbol: 'SPY', name: 'S&P 500' },
  { symbol: 'QQQ', name: 'NASDAQ 100' },
  { symbol: 'DIA', name: 'Dow Jones' },
];

export const MarketIndices: React.FC = () => {
  const [indices, setIndices] = useState<MarketIndex[]>(MARKET_INDICES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const svc = new TradierService();

  const fetchMarketData = async () => {
    try {
      setError(null);
      const quotes = await svc.getQuotes(MARKET_INDICES.map(i => i.symbol));
      setIndices(MARKET_INDICES.map(idx => ({ ...idx, quote: quotes.find(q => q.symbol === idx.symbol) })));
      setLastUpdated(new Date());
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch market data';
      setError(msg);
      showToast('Failed to load market indices', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMarketData(); }, []);

  const fmt$ = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
  const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1px', background: '#1e2d45' }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ background: '#111827', padding: '1.25rem' }}>
            <div style={{ height: 12, width: 80, borderRadius: 4, marginBottom: 10 }} className="skeleton" />
            <div style={{ height: 10, width: 40, borderRadius: 3, marginBottom: 16 }} className="skeleton" />
            <div style={{ height: 28, width: 120, borderRadius: 5, marginBottom: 10 }} className="skeleton" />
            <div style={{ height: 10, width: 90, borderRadius: 3, marginBottom: 6 }} className="skeleton" />
            <div style={{ height: 10, width: 60, borderRadius: 3 }} className="skeleton" />
          </div>
        ))}
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#f87171', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
          Unable to load market data
        </p>
        <button
          onClick={fetchMarketData}
          style={{ background: '#3b82f6', color: '#fff', padding: '0.5rem 1rem', borderRadius: 8, fontSize: '0.8125rem', fontWeight: 600, border: 'none', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1px',
        background: '#1a2235',
      }}>
        {indices.map((idx) => {
          const q = idx.quote;
          const isPos = q ? q.change > 0 : null;
          const isNeg = q ? q.change < 0 : null;
          const changeColor = isPos ? '#10b981' : isNeg ? '#ef4444' : '#94a3b8';
          const changeBg    = isPos ? 'rgba(16,185,129,0.1)' : isNeg ? 'rgba(239,68,68,0.1)' : 'rgba(148,163,184,0.08)';
          const changeBorder = isPos ? 'rgba(16,185,129,0.25)' : isNeg ? 'rgba(239,68,68,0.25)' : 'rgba(148,163,184,0.15)';
          const Icon = isPos ? TrendingUp : isNeg ? TrendingDown : Minus;

          return (
            <div
              key={idx.symbol}
              onClick={() => navigate(`/stock/${idx.symbol}`)}
              style={{
                background: '#111827',
                padding: '1.25rem 1.5rem',
                cursor: 'pointer',
                transition: 'background 0.18s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#141e30'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#111827'; }}
            >
              {/* Name + symbol */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#e2e8f0', letterSpacing: '-0.01em', marginBottom: 2 }}>
                    {idx.name}
                  </div>
                  <div style={{ fontSize: '0.72rem', fontFamily: "'JetBrains Mono', monospace", color: '#334155', fontWeight: 600, letterSpacing: '0.04em' }}>
                    {idx.symbol}
                  </div>
                </div>
                {q && (
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: changeBg, border: `1px solid ${changeBorder}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={14} color={changeColor} strokeWidth={2.5} />
                  </div>
                )}
              </div>

              {q ? (
                <>
                  {/* Price */}
                  <div style={{
                    fontSize: '1.625rem', fontWeight: 800, color: '#f1f5f9',
                    letterSpacing: '-0.04em', lineHeight: 1.1, marginBottom: '0.625rem',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {fmt$(q.price)}
                  </div>

                  {/* Change row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.625rem' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: changeColor, fontFamily: "'JetBrains Mono', monospace" }}>
                      {q.change >= 0 ? '+' : ''}{fmt$(q.change)}
                    </span>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 700,
                      padding: '0.125rem 0.5rem', borderRadius: 99,
                      background: changeBg, border: `1px solid ${changeBorder}`,
                      color: changeColor,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>
                      {fmtPct(q.change_percent)}
                    </span>
                  </div>

                  {/* Volume */}
                  <div style={{ fontSize: '0.72rem', color: '#334155', fontWeight: 500 }}>
                    Vol: {q.volume.toLocaleString()}
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  <div style={{ height: 28, width: 110, borderRadius: 4 }} className="skeleton" />
                  <div style={{ height: 12, width: 80, borderRadius: 3 }} className="skeleton" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer strip */}
      {!error && indices.every(i => i.quote) && lastUpdated && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.5rem 1.25rem',
          borderTop: '1px solid #1a2235',
          background: '#0c111d',
        }}>
          <span style={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 500 }}>
            Real-time · Tradier
          </span>
          <span style={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 500 }}>
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        </div>
      )}
    </>
  );
};
